'use client';
import { JSX, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import classNames from 'classnames';

// components
import { SwiperSlide } from 'swiper/react';
import { AtHeader } from '@/components/atoms/at-header/at-header';
import { AtText } from '@/components/atoms/at-text/at-text';
import { AtClickable } from '@/components/atoms/at-clickable/at-clickable';
import { AtCarousel } from '@/components/atoms/at-carousel/at-carousel';
import { MlCard } from '@/components/molecules/ml-card/ml-card';
import { MlExteriorDrawer } from '@/components/molecules/ml-exterior-drawer/ml-exterior-drawer';

// hooks
import useBreakpoint from '@/hooks/useBreakpoint';

// types
import { EClickableTypes } from '@/components/atoms/at-clickable/at-clickable.types';
import {
  IExteriorStyles,
  IOrExteriorSwitcherProps,
} from './or-exterior-switcher.types';
import { IAtMediaProps } from '@/components/atoms/at-media/at-media.types';
import { EMlCardsTypes } from '@/components/molecules/ml-card/ml-card.types';

/**
 * Main component for rendering a selectable exterior style carousel.
 * Includes a selected card, desktop carousel, and mobile drawer.
 */
export const OrExteriorSwitcher = ({
  exteriorCount,
  exteriorStyles,
  copy,
  selectedExterior,
  onExteriorSelect,
}: IOrExteriorSwitcherProps): JSX.Element => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const handleMobileCardChange = () => {
    setIsDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
  };

  return (
    <div data-theme="light" className="w-full bg-skin-secondary py-5">
      <div className="container grid grid-cols-4 items-center lg:grid-cols-12">
        <div className="col-span-2 col-start-1 items-center text-center lg:col-span-1">
          <AtHeader className="mb-17" element="p" variant="h2">
            {exteriorCount}
          </AtHeader>
          <AtText size="lg">{copy.contentDescription}</AtText>
          {/* change exterior button for mobile */}
          <AtClickable
            className="mt-3 lg:hidden"
            type={EClickableTypes.LINK}
            onClick={handleMobileCardChange}
            interactLocation="or-exterior-switcher"
          >
            Change
          </AtClickable>
        </div>
        {/* selected exterior */}
        <div className="col-span-2 col-start-3 lg:col-span-4 lg:col-start-2 lg:ml-4 lg:mr-9">
          <SelectedCard card={selectedExterior} />
        </div>
        {/* carousel */}
        <div className="col-span-6 hidden lg:col-span-6 lg:col-start-6 lg:block">
          <Carousel onCardClick={onExteriorSelect} cards={exteriorStyles} />
        </div>
      </div>
      <MobileDrawer
        cards={exteriorStyles}
        onCardClick={onExteriorSelect}
        onClose={handleDrawerClose}
        isOpen={isDrawerOpen}
        drawerTitle={copy.drawerTitle}
        drawerSubtitle={copy.drawerSubtitle}
      />
    </div>
  );
};

interface ICard {
  bodyText: string;
  image: IAtMediaProps;
  name: string;
  selected: boolean;
}

/**
 * Reusable card component used to display an exterior style.
 * Used in carousel, drawer, and selected display states.
 */
const Card = ({ bodyText, image, name, selected }: ICard) => {
  return (
    <MlCard
      body={bodyText}
      imageProps={image}
      selected={selected}
      title={name}
      headerElement="p"
      headerVariant="h4"
      variant={EMlCardsTypes.STAT_CARD}
      tabIndex={selected ? undefined : 0}
      className={classNames({ 'rounded-none': !selected })}
    />
  );
};

/**
 * Displays the currently selected exterior style.
 * Includes transition animation and adjusts body text based on viewport size.
 */
const SelectedCard = ({ card }: { card: IExteriorStyles | null }) => {
  const bp = useBreakpoint();
  const isMobile = bp === 'xs' || bp === 'sm' || bp === 'md';

  return (
    <AnimatePresence mode="wait">
      {card && (
        <motion.div
          key={card.id}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { duration: 0.2 } }}
          exit={{ opacity: 0, transition: { duration: 0.25 } }}
        >
          <Card
            name={card.name}
            image={{ ...card.image, priority: true, quality: 50 }}
            selected
            bodyText={isMobile ? 'Current' : 'Current Selection'}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

interface ICarousel {
  cards: IExteriorStyles[];
  onCardClick: (card: IExteriorStyles) => void;
}

/**
 * Desktop-only horizontal carousel of exterior styles.
 * Adjusts layout based on number of items and handles click/keyboard selection.
 */
const Carousel = ({ cards, onCardClick }: ICarousel) => {
  const isCompactCarousel = cards?.length <= 2;

  return (
    <AtCarousel
      breakpoints={{
        1024: { slidesPerView: isCompactCarousel ? cards.length : 2.25 },
        768: { slidesPerView: isCompactCarousel ? cards.length : 1.25 },
        0: { slidesPerView: 1 },
      }}
      buttonPosition={isCompactCarousel ? 'none' : 'end'}
      containerClasses="flex"
      contentClasses="flex flex-row"
      showDivider={!isCompactCarousel}
      slidesPerView={isCompactCarousel ? cards.length : 2.25}
      swiperClasses={isCompactCarousel ? 'mx-3' : ''}
    >
      {cards?.map((card: IExteriorStyles) => {
        return (
          <SwiperSlide
            aria-label={`Select exterior style ${card.name}`}
            className="h-full min-w-320 cursor-pointer p-1"
            data-testid="swiper-slide"
            key={card.id}
            role="button"
            onClick={() => onCardClick(card)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                onCardClick(card);
              }
            }}
          >
            <Card
              name={card.name}
              image={card.image}
              selected={false}
              bodyText="Select"
            />
          </SwiperSlide>
        );
      })}
    </AtCarousel>
  );
};

interface IMobileDrawer {
  cards: IExteriorStyles[];
  drawerTitle: string;
  drawerSubtitle: string;
  isOpen: boolean;
  onClose: () => void;
  onCardClick: (card: IExteriorStyles) => void;
}

/**
 * Mobile-only drawer UI for selecting an exterior style.
 * Closes on selection and passes updated value to parent callback.
 */
const MobileDrawer = ({
  cards,
  onCardClick,
  isOpen,
  drawerTitle,
  drawerSubtitle,
  onClose,
}: IMobileDrawer) => {
  const handleCardClick = (card: IExteriorStyles) => {
    onClose();
    onCardClick(card);
  };

  return (
    <MlExteriorDrawer
      title={drawerTitle}
      subtitle={drawerSubtitle}
      isOpen={isOpen}
      onClose={onClose}
    >
      {cards?.map((card: IExteriorStyles) => {
        return (
          <div
            aria-label={`Select exterior style ${card.name}`}
            className="mb-8"
            key={card.id}
            role="button"
            onClick={() => handleCardClick(card)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') handleCardClick(card);
            }}
          >
            <Card
              name={card.name}
              image={card.image}
              selected={false}
              bodyText="Select"
            />
          </div>
        );
      })}
    </MlExteriorDrawer>
  );
};

OrExteriorSwitcher.Card = Card;
OrExteriorSwitcher.SelectedCard = SelectedCard;
OrExteriorSwitcher.Carousel = Carousel;
OrExteriorSwitcher.MobileDrawer = MobileDrawer;
